import { useState } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBlogs } from "../redux/Action";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Pagination from "@mui/material/Pagination";
import usePagination from "../Hooks/usePagination";
import BlogsCard from "../Components/BlogsCard";
import Footer from "../Components/v1/Footer";
import { months } from "../Public/constants";
import { BsSearch } from "react-icons/bs";

const Blogs = () => {
  const { isLoading, isError, blogs, blogCount } = useSelector(
    (state) => state,
  );

  const dispatch = useDispatch();

  let [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const PER_PAGE = 9;

  const count = Math.ceil(blogs.length / PER_PAGE);
  const { jump, currentData } = usePagination(blogs, PER_PAGE);

  const handleChange = (e, p) => {
    setPage(p);
    jump(p);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    document.title = "Blogshog | Global Index";
    getBlogs(dispatch, debouncedSearch);
  }, [dispatch, debouncedSearch]);

  useEffect(() => {
    setPage(1);
    jump(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  return (
    <div className="flex flex-col min-h-screen dark:bg-[#0a0a0a] text-black dark:text-white mt-[73px]">
      {/* Brutalist Header Area */}
      <div className="bg-white dark:bg-black border-b border-black dark:border-white/30">
        <div className="px-8 md:px-16 py-8 md:py-10 flex flex-col md:flex-row md:items-end justify-between">
          <div>
            <span className="inline-block border border-black dark:border-white/30 px-3 py-1 text-[10px] font-mono font-bold tracking-widest uppercase mb-8 bg-black dark:bg-white text-white dark:text-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] dark:shadow-[2px_2px_0px_0px_rgba(255,255,255,0.5)]">
              SYS.CAT.01 — Feed
            </span>
            <h1 className="text-5xl lg:text-6xl font-black uppercase tracking-tighter leading-[0.9] text-black dark:text-white m-0 p-0">
              BLOGS
            </h1>
          </div>
          {/* Brutalist Search Bar */}
          <div className="max-sm:py-4 sm:px-8 flex-1">
            <div className="flex items-center bg-white dark:bg-black border-2 border-black dark:border-white/30 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[4px_4px_0px_0px_rgba(255,255,255,0.5)] focus-within:translate-x-1 focus-within:translate-y-1 focus-within:shadow-none transition-all w-full max-w-4xl mx-auto">
              <div className="px-4 py-3 sm:py-4 border-r-2 border-black dark:border-white/30 bg-black text-white dark:bg-white dark:text-black flex items-center justify-center">
                <BsSearch className="text-xl" />
              </div>
              <input
                type="text"
                placeholder="QUERY DATABANK BY TOPIC, OR KEYWORD..."
                className="w-full bg-transparent px-4 py-3 sm:py-4 outline-none text-xs sm:text-sm font-mono font-bold uppercase tracking-widest text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-600"
                onInput={(e) => setSearch(e.target.value)}
                value={search}
              />
            </div>
          </div>
          <div className="mt-8 md:mt-0 max-w-xs text-xs font-mono tracking-widest text-gray-500 dark:text-gray-400 uppercase font-bold text-right border-l-4 border-black dark:border-white/30 pl-4">
            Access point for all published operational artifacts across the
            primary databank.
          </div>
        </div>
      </div>

      <div className="flex-grow px-2 sm:px-14 py-14">
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              marginTop: "20vh",
              height: "100%",
            }}
          >
            <CircularProgress color="inherit" />
          </Box>
        ) : isError ? (
          <div className="p-8 font-mono border border-red-500 text-red-500 m-8 bg-red-50 dark:bg-red-950">
            CRITICAL ERROR: DATABANK CORRUPTED OR OFFLINE.
          </div>
        ) : blogs.length > 0 ? (
          <>
            <section className="bg-white dark:bg-black">
              <div className="">
                {/* Added bg-black container to make gaps appear as black borders */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {currentData().map((el, i) => {
                    const date = el.createdAt.substring(8, 10);
                    const month_in_num =
                      (el.createdAt.substring(5, 7) * 10) / 10;
                    const month = months[month_in_num - 1];
                    return (
                      !el.softDelete && (
                        <BlogsCard
                          date={date}
                          key={el._id || i}
                          month={month}
                          el={el}
                        />
                      )
                    );
                  })}
                </div>
              </div>
              {blogCount > PER_PAGE && (
                <div className="flex justify-center p-8 border-t border-black dark:border-white/30 bg-white dark:bg-black">
                  <Stack spacing={2}>
                    <Pagination
                      count={count}
                      variant="outlined"
                      shape="rounded"
                      page={page}
                      onChange={handleChange}
                      sx={{
                        "& .MuiPaginationItem-root": {
                          fontFamily:
                            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                          fontWeight: "bold",
                          borderRadius: "0",
                          borderColor: "currentColor",
                          color: "currentColor",
                        },
                        "& .Mui-selected": {
                          backgroundColor:
                            "var(--tw-prose-body, black) !important",
                          color:
                            "var(--tw-prose-invert-body, white) !important",
                        },
                      }}
                      className="dark:[&_.MuiPaginationItem-root]:text-white dark:[&_.MuiPaginationItem-root]:border-white dark:[&_.Mui-selected]:!bg-white dark:[&_.Mui-selected]:!text-black"
                    />
                  </Stack>
                </div>
              )}
            </section>
          </>
        ) : (
          <div className="p-16 border-b border-black dark:border-white/30 text-center font-mono uppercase tracking-widest text-xs sm:text-sm bg-white dark:bg-black text-black dark:text-white">
            {search ? (
              <>
                <span className="text-red-600 dark:text-red-500 font-black">
                  [ QUERY FAILED ]
                </span>{" "}
                : NO ARTIFACTS FOUND MATCHING "{search.toUpperCase()}".
              </>
            ) : (
              <>[ EMPTY DATABANK ] : NO OPERATIONAL ARTIFACTS RECORDED.</>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Blogs;
